import "./css/newUpsell.scss";
import "./css/animations.scss";

import {
  ListSection,
  BannerPrice,
  UpsellIconSection,
  StaticImgText,
  Typography,
  StepBar,
  GlobalIcon,
} from "get-life-storybook-ts";

import UpgradeCapitalModal from "./Components/UpgradeCapitalModal/UpgradeCapitalModal";
import MaxCapitalModal from "../../components/MaxCapitalModal/MaxCapitalModal";
import ConditionsModalAB from "../../components/ConditionsModal/ConditionsModalAB";
import PendingCapitalAdjustmentModal from "../../components/PendingCapitalAdjustmentModal/PendingCapitalAdjustmentModal";
import PriceSummaryCard from "./Components/PriceSummaryCard";
import PolicyCustomizer from "./Components/PolicyCustomizer";
import UpsellCTA from "./Components/UpsellCTA";
import UpsellStickyBar from "./Components/UpsellStickyBar";
import TrustpilotReviews from "./Components/TrustpilotReviews/TrustpilotReviews";

import UpsellController from "./Upsell.controller";
import { QuestionTreeErrorPanel } from "../Questions/QuestionTreeError";
import { useUpsellDisplayData } from "./hooks/useUpsellDisplayData";
import { IconSection, stepList, Truspilot } from "../../utils/dataForNewUpsell";
import { GlobalShowTruspilot } from "../../utils/InternationlChanges";
import { LanguageT } from "../../context/GetLifeContext.context";
import { DOMAIN } from "../../constants/Global";
import { PurchasePeriodT } from "../../api/request/Lead/Interfaces/DataInterface.response";
// TODO: validar copy con legal — D.lgs. 209/2025 / EU 2023/2673 (deadline 19/06/2026)
import {
  ITComplianceOnly,
  IntermediaryBadge,
  PriceTransparencyNote,
  PrecontractualDocs,
  WithdrawalRightNotice,
} from "../../components/Compliance";

const UpsellV2 = () => {
  const {
    correctPrice,
    downloadDocument,
    datePickerProps,
    handleCallMe,
    handleUpgradedClose,
    priceChange,
    isMobile,
    listSectionData,
    renderPriceValidation,
    selectPlan,
    setChangeDone,
    translate,
    upGradedOpen,
    downloadableFiles,
    isABtesting,
    afterPromoText,
    beforePromoText,
    isStickyVisible,
    product,
    nodeConditionsModal,
    handleConditions,
    couponInfo,
    traitState,
    pendingAdjustmentModal,
    gtf_campaign,
    isSubmitting,
    questionTreeError,
    retryQuestionTree,
    isRetryingQuestionTree,
  } = UpsellController();

  const TRAIT_LABELS: Record<string, string> = {
    DEATH: translate("trait.DEATH") || "Fallecimiento",
    DISABILITY: translate("trait.DISABILITY") || "Incapacidad",
    SERIOUS_ILLNESS: translate("trait.SERIOUS_ILLNESS") || "Enfermedad grave",
    MALATTIA_TERMINALE: translate("trait.MALATTIA_TERMINALE") || "Malattia terminale",
  };

  const { guaranteeOptions, handleGuaranteeToggle, handleCapitalChange, chipsData, priceData, getDiscountedMonthlyPrice } =
    useUpsellDisplayData({
      product,
      translate,
      traitLabels: TRAIT_LABELS,
      capitalsByTrait: traitState.capitalsByTrait,
      traitBoundaries: traitState.traitBoundaries,
      traitLoading: traitState.loading,
      isTraitActive: traitState.isTraitActive,
      toggleTrait: traitState.toggleTrait,
      setTraitCapital: traitState.setTraitCapital,
      beforePromoText,
      afterPromoText,
      couponInfo,
      handleConditions,
    });

  const mandatoryGuarantees = guaranteeOptions.filter((g) => !g.enabled);
  const optionalGuarantees = guaranteeOptions.filter((g) => g.enabled);

  if (null !== questionTreeError) {
    return (
      <QuestionTreeErrorPanel
        kind={questionTreeError}
        translate={translate}
        onRetry={retryQuestionTree}
        retrying={isRetryingQuestionTree}
      />
    );
  }

  if (!product) return null;

  return (
    <>
      <div className="payment-newUpsellContainer">
        {(gtf_campaign === "VIDAAUTONOMO" || product?.promotion?.promotionalCode === "VIDAAUTONOMO") && (
          <div className="banner-autonomo w-full text-white py-4 px-6">
            <div className="max-w-[960px] mx-auto flex items-center gap-4">
              <GlobalIcon iconName="ShieldCheckIcon" size="L" color="white" />
              <div>
                <Typography
                  variant="Body"
                  weight="700"
                  text={translate("upsell.banner.autonomo.title")}
                  className="text-white"
                />
                <Typography
                  variant="BodySmall"
                  weight="400"
                  text={translate("upsell.banner.autonomo.subtitle")}
                  className="text-white opacity-90 mt-1"
                />
              </div>
            </div>
          </div>
        )}
        <div className="newUpsell--section1">
          <div className="newUpsell-banner--section">
            <BannerPrice
              title={translate("newUpsell.banner.title")}
              subtitle={translate("newUpsell.banner.subtitle").replace(
                "{{capital}}",
                new Intl.NumberFormat(
                  process.env.REACT_APP_DOMAIN_LOCALE === "fr"
                    ? "fr-FR"
                    : "es-ES"
                ).format(traitState.capitalsByTrait["DEATH"] ?? 0)
              )}
              homeImage="https://storage.googleapis.com/getlife-bucket-1/public/image/img-new-web/Illustration_woman.svg"
              background="white"
            />
          </div>
        </div>

        {/* Policy customization + Price summary */}
        <div
          className="payment-newUpsellContainer__newsection"
          id="upsell_page_resume_cart"
        >
          <div className="flex flex-col items-center justify-center md:bg-blue-gray-100 w-full md:p-[40px]">
            {!isMobile && (
              <div className="w-full pb-[25px]">
                <StepBar step={stepList(translate)} />
              </div>
            )}

            <div className="w-full flex max-w-[1120px] flex-col md:flex-row gap-x-[3px]">
              <div className="w-full md:w-[40%]">
                <PriceSummaryCard
                  titlePrice={
                    isMobile
                      ? ""
                      : translate("newUpsell.resumeCart.titlePrice")
                  }
                  price={priceData}
                  tags={chipsData}
                  buttonText={translate(
                    "aggregator.personalInformation.btnContract"
                  )}
                  onClick={() => selectPlan()}
                  priceChange={priceChange}
                  subtitle={
                    isABtesting ? renderPriceValidation() : undefined
                  }
                  tooltipText={
                    isABtesting ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: translate(
                            "newUpsell.resumeCart.tooltipValidDate"
                          ),
                        }}
                      />
                    ) : undefined
                  }
                  trustpilot={translate("newUpsellV2.priceCard.trust")}
                  cancelAnytimeText={translate(
                    "newUpsellV2.priceCard.cancelAnytime"
                  )}
                  showTrustpilot={GlobalShowTruspilot[DOMAIN as LanguageT]}
                />
                <ITComplianceOnly>
                  <PriceTransparencyNote translate={translate} />
                </ITComplianceOnly>
              </div>

              {isMobile && (
                <div className="w-full pb-[25px]">
                  <StepBar step={stepList(translate)} />
                </div>
              )}

              <div className="w-full md:w-[60%] bg-white">
                <PolicyCustomizer
                  title={translate("newUpsell.resumeCart.titleCP")}
                  translate={translate}
                  changeCapitalOverlay={undefined}
                  changeCapitalTitle={
                    <Typography
                      variant="H3"
                      weight="700"
                      className="text-[#424242]"
                      text={translate("newUpsell.tooltipCapital.title")}
                    />
                  }
                  changeCapitalText={undefined}
                  onChangeCapitalOpen={() => setChangeDone(false)}
                  datePickerProps={datePickerProps()}
                  downloadableFiles={downloadableFiles}
                  downloadableFilesTitle={translate(
                    "downloadableFiles.title"
                  )}
                  downloadDocument={downloadDocument}
                  downloadableLoading={false}
                  mobileButtonText={translate(
                    "aggregator.personalInformation.btnContract"
                  )}
                  onMobileButtonClick={() => selectPlan()}
                  isMobile={isMobile}
                  guarantees={mandatoryGuarantees}
                  optionalGuarantees={optionalGuarantees}
                  onToggleGuarantee={handleGuaranteeToggle}
                  onCapitalChange={handleCapitalChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Trustpilot */}
        {GlobalShowTruspilot[DOMAIN as LanguageT] && (
          <div className="payment-newUpsellContainer__newsection">
            <TrustpilotReviews
              comments={Truspilot(translate)}
              excelentText={translate("newUpsell.truspilotSection.excelentText")}
              rating={4.8}
              title={translate("newUpsell.truspilotSection.title")}
              trustpilotText={translate("newUpsell.truspilotSection.trustpilotText")}
            />
          </div>
        )}

        {/* Coverage details */}
        <div className="payment-newUpsellContainer__newsection">
          <div className="containerSection">
            <ITComplianceOnly>
              <IntermediaryBadge translate={translate} />
            </ITComplianceOnly>
            <ListSection
              title={translate("newUpsell.listSection.title")}
              list={listSectionData}
            />
          </div>
        </div>

        {/* Icons section */}
        <div className="payment-newUpsellContainer__newsection">
          <UpsellIconSection
            title={translate("newUpsell.iconSection.title")}
            data={IconSection(translate) as any}
          />
        </div>

        {/* Static content */}
        <div className="payment-newUpsellContainer__newsection">
          <StaticImgText
            image={translate("newUpsell.staticImgText.img")}
            title={translate("newUpsell.staticImgText.title")}
            titleMobile={translate("newUpsell.staticImgText.titleMobile")}
            paragraphFirst1={translate("newUpsell.staticImgText.paragraphFirst1")}
            paragraphFirst2={translate("newUpsell.staticImgText.paragraphFirst2")}
            paragraphFirst3={translate("newUpsell.staticImgText.paragraphFirst3")}
            paragraphFirst4={translate("newUpsell.staticImgText.paragraphFirst4")}
            paragraphSecond1={translate("newUpsell.staticImgText.paragraphSecond1")}
            paragraphSecond2={translate("newUpsell.staticImgText.paragraphSecond2")}
            paragraphSecond3={translate("newUpsell.staticImgText.paragraphSecond3")}
            paragraphSecond4={translate("newUpsell.staticImgText.paragraphSecond4")}
            paragraphSecond5={translate("newUpsell.staticImgText.paragraphSecond5")}
          />
        </div>

        <ITComplianceOnly>
          <div className="payment-newUpsellContainer__newsection">
            <PrecontractualDocs translate={translate} />
            <WithdrawalRightNotice translate={translate} />
          </div>
        </ITComplianceOnly>

      </div>

      {/* CTA section */}
      <UpsellCTA
        translate={translate}
        isMobile={isMobile}
        isABtesting={isABtesting}
        chipsData={chipsData}
        priceData={priceData}
        selectPlan={selectPlan}
        handleCallMe={handleCallMe}
        renderPriceValidation={renderPriceValidation}
        loading={priceChange || traitState.loading || isSubmitting}
      />

      {/* Mobile sticky bar */}
      {isMobile && isStickyVisible && (
        <UpsellStickyBar
          product={product}
          translate={translate}
          isABtesting={isABtesting}
          chipsData={chipsData}
          selectPlan={selectPlan}
          correctPrice={correctPrice}
          getDiscountedMonthlyPriceWithCurrency={() =>
            `${getDiscountedMonthlyPrice()} €`
          }
          loading={priceChange || traitState.loading || isSubmitting}
        />
      )}

      {/* Modals */}
      <UpgradeCapitalModal
        upGradedOpen={upGradedOpen}
        handleUpgradedClose={handleUpgradedClose}
        capitalsByTrait={{
          DEATH: traitState.capitalsByTrait?.DEATH || product.capital,
        }}
      />
      <MaxCapitalModal capital={product.capital} />
      <PendingCapitalAdjustmentModal {...pendingAdjustmentModal} />
      {product.promotion && (
        <ConditionsModalAB
          couponInfo={couponInfo}
          nodeFakeDoorModal={nodeConditionsModal}
          promotion={product.promotion as any}
          collection={product.feeCollection as any}
          period={
            (product.period?.toLowerCase() || "monthly") as PurchasePeriodT
          }
          handleSubmit={() => nodeConditionsModal.current?.handleShowPortal()}
        />
      )}
    </>
  );
};

export default UpsellV2;
